<script setup lang="ts">
/* L'UNIQUE dialogue de confirmation de l'application — monté une fois dans
 * App.vue, piloté impérativement par `useConfirm()` (spec §9.2).
 *
 * Comportement repris de app/components/ConfirmDialog.vue :
 *  - focus par défaut sur Annuler (posé par AlertDialogContent sur l'élément
 *    enregistré par AlertDialogCancel) ;
 *  - Escape = annuler (fermeture émise par la primitive) ;
 *  - clic sur l'overlay = annuler (la primitive l'empêche de fermer, on le
 *    traite donc nous-mêmes) ;
 *  - message en `white-space: pre-line`.
 *
 * ⚠️ Toute fermeture venant de la primitive vaut ANNULATION : le bouton de
 * confirmation ne ferme pas de lui-même, il appelle `accept()`. C'est ce qui
 * garantit qu'une même promesse ne peut pas être tranchée deux fois.
 */
// ⚠️ `AlertDialogCancel` vient de `reka-ui` et NON du wrapper shadcn : celui-ci
// pose `buttonVariants({ variant: 'outline', size: 'default' })` en classe de
// repli, et reka-ui FUSIONNE les attributs avec l'enfant d'`asChild` au lieu de
// les remplacer — le bouton Annuler héritait donc de `h-8` (32 px figés) quand
// Confirmer, bouton nu, mesure ~35,5 px. Les deux boutons de ce dialogue sont
// une paire : ils partagent le gabarit `.cfd-btn` et rien d'autre.
import { AlertDialogCancel } from 'reka-ui';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useConfirmDialogState } from '@/composables/useConfirm';

const { isOpen, options, accept, cancel } = useConfirmDialogState();

function onOpenChange(open: boolean): void {
  if (!open) cancel();
}
</script>

<template>
  <AlertDialog :open="isOpen" @update:open="onOpenChange">
    <AlertDialogContent
      class="cfd max-w-[420px] rounded-[10px] border border-pline-2 bg-pbg-1 p-[22px_24px] text-ptext"
      @pointerDownOutside="cancel"
    >
      <AlertDialogHeader class="block text-left">
        <AlertDialogTitle class="text-[15px] font-medium tracking-tight text-ptext">
          {{ options.title }}
        </AlertDialogTitle>
        <AlertDialogDescription class="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-ptext-2">
          {{ options.message }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div class="mt-5 flex justify-end gap-2.5">
        <AlertDialogCancel asChild>
          <button type="button" class="cfd-btn cfd-cancel">
            {{ options.cancelLabel }}
          </button>
        </AlertDialogCancel>
        <button
          type="button"
          :class="['cfd-btn', options.danger ? 'cfd-danger' : 'cfd-confirm']"
          @click="accept"
        >
          {{ options.confirmLabel }}
        </button>
      </div>
    </AlertDialogContent>
  </AlertDialog>
</template>

<style scoped>
/* Boutons repris tels quels de app/components/ConfirmDialog.vue. */
.cfd-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: var(--radius);
  font-size: 13px;
  font-weight: 500;
  font-family: var(--sans);
  cursor: pointer;
  transition: filter 0.15s, color 0.15s, border-color 0.15s;
}
.cfd-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.cfd-cancel {
  background: var(--bg-2);
  color: var(--text-2);
  border: 1px solid var(--line-2);
}
.cfd-cancel:hover:not(:disabled) { color: var(--text); border-color: var(--text-4); }
.cfd-confirm {
  background: var(--accent);
  border: 1px solid var(--accent-dim);
  color: #fff;
}
.cfd-confirm:hover:not(:disabled) { filter: brightness(1.08); }
.cfd-danger {
  background: oklch(0.55 0.18 25);
  border: 1px solid oklch(0.55 0.18 25);
  color: #fff;
}
.cfd-danger:hover:not(:disabled) { filter: brightness(1.1); }
</style>
